import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { IconCheck, IconSearch } from '../icons/index.js';
import { cn } from '../lib/cn.js';
import { Input } from './Input.js';
import type { Tone } from '../views/contract.js';
import { searchField } from '../views/field-query.js';

/**
 * LA LISTE D'OPTIONS DU SOCLE, en remplacement du `select` natif.
 *
 * POURQUOI PAS LE NATIF : il demandait DEUX clics (le premier armait la case,
 * le second ouvrait le panneau, parce que donner le focus a un `select` ne le
 * deroule pas), son panneau est dessine par le systeme, hors de la page, donc
 * ni a nos tokens ni a notre typographie, et sa hauteur nous echappait. Ici :
 * un seul clic, la liste s'ouvre sous le champ (ou au-dessus, quand le bas
 * manque).
 *
 * ELLE VIT ICI DEPUIS LE LOT 2 DES LIENS ENTRE PORTES, et son ancien en-tete
 * l'annoncait deja (« ce sera le moment d'en faire une primitive de `@taki/ui`
 * plutot qu'un composant de cette vue ») : le SELECTEUR DE FICHE LIEE est la
 * meme liste, avec une recherche et une entree qui detache. Deux listes
 * deroulantes maison auraient diverge au premier ajustement, et c'est le
 * clavier qui se serait mis a repondre differemment d'un champ a l'autre.
 *
 * ELLE EST POSEE EN ABSOLU, ancree a la case : elle ne pousse rien. L'ancre
 * garde le texte du champ a sa place, avec l'anneau d'edition ; la case ne se
 * transforme pas en controle, elle s'allume.
 *
 * ELLE NE DECIDE PAS DE L'ECRITURE. Elle remonte un choix (`onPick`) ou une
 * fermeture (`onClose`) : quand ecrire appartient a l'appelant, comme pour tous
 * les editeurs du socle.
 */

/** Une entree de la liste. Une `Option` du contrat s'y assigne telle quelle. */
export interface OptionListEntry {
  /** La valeur ECRITE quand on choisit cette entree. */
  value: string;
  /** Le mot sous les yeux. */
  label: string;
  /** Une seconde ligne, plus discrete : la reference, l'email, le sous-titre. */
  detail?: string;
  /**
   * LE CHEMIN DEVANT LE MOT, attenue : « Projet X › » devant « Tâche Y ». Une
   * meme liste montre alors des entrees de deux niveaux (un projet, et chacune
   * de ses taches), et la recherche le lit comme le libelle : taper le nom
   * d'un projet ramene le projet ET ses taches.
   */
  prefixe?: string;
  /**
   * ACCEPTE POUR QU'UNE `Option` PASSE TELLE QUELLE, jamais peint : une liste
   * de choix marque celle qu'on a CHOISIE (la coche), pas le ton de chacune.
   * Peindre douze tons dans un panneau de 18rem ferait chercher un sens qui
   * n'existe pas.
   */
  tone?: Tone;
  /** Entree INACTIVE : grisee, non choisissable, sa raison au survol (G16). */
  inactif?: string;
  /**
   * Entree ATTENUEE, mais choisissable : la valeur en place qui n'est plus
   * proposee (« Hors des choix »). Son libelle passe au gris, rien d'autre ne
   * change.
   */
  attenue?: boolean;
  /**
   * DES MOTS QUE LA RECHERCHE LIT SANS LES MONTRER : la clef stockee d'un choix
   * de liste (`cm` sous « Community management »). Celui qui connait la clef la
   * tape, et il la trouve ; personne ne la lit a l'ecran.
   */
  motsCles?: string;
  /**
   * LE TITRE DU GROUPE de l'entree (« En cours »). Un titre se pose au-dessus
   * de la premiere entree de chaque groupe, dans l'ordre donne par l'appelant :
   * la liste ne trie rien, elle nomme les paquets qu'on lui a faits. Un titre
   * n'est pas une entree : ni curseur, ni choix.
   */
  groupe?: string;
}

/**
 * AU-DELA DE CE NOMBRE D'ENTREES, UNE LISTE SE CHERCHE. En deca, le champ de
 * recherche coute plus qu'il ne rapporte : un panneau de cinq lignes se lit
 * d'un coup d'oeil, et une frappe de plus avant de choisir se paie a chaque
 * ouverture.
 */
export const SEUIL_RECHERCHE = 8;

/**
 * LE LIBELLE PAR DEFAUT D'UN ECHEC. Sa raison exacte reste au survol.
 *
 * DEFAUT, PLUS EN DUR : la liste sert aussi des gestes qui ECRIVENT (« Lier une
 * fiche existante »), et un rattachement refuse affichait « Lecture impossible »,
 * c'est-a-dire le mauvais geste. L'appelant qui sait lequel a echoue le dit par
 * `libelleErreur` ; celui qui ne dit rien lit une LECTURE, qui est le cas de
 * tous les catalogues.
 */
const LECTURE_IMPOSSIBLE = 'Lecture impossible';

/**
 * LE FILTRE DE LA RECHERCHE, sur `label`, `detail`, `prefixe` et `motsCles`.
 *
 * CLIENT, SUR CE QUI EST CHARGE, et c'est une limite assumee : Firestore n'a
 * pas de plein texte, l'annuaire qui nourrit cette liste est plafonne, donc
 * au-dela du plafond une fiche existe et ne se trouve pas.
 *
 * SANS CASSE NI ACCENTS, DES DEUX COTES (`searchField` : « medi » trouve
 * « Médicaments », « médic » trouve « Medicaments »), ET MOT PAR MOT : chaque
 * mot tape doit se trouver dans l'un des textes de l'entree, pas forcement le
 * meme (« Taki novembre » trouve la tache dont le chemin dit « Taki SARL ·
 * Prestations novembre »). Pour un seul mot,
 * c'est exactement la regle de la recherche d'ecran (`matchesSearch`,
 * `views/project.ts`) ; a plusieurs mots, elle ne perd aucune entree que
 * l'ancienne regle trouvait (une phrase contenue dans un texte y contient
 * chacun de ses mots), elle en trouve davantage.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est la seule chose de cette liste qui
 * puisse se tromper en silence (un mot qui ne trouve rien ressemble a une liste
 * vide), et un test ne peut la tenir que sans React ni DOM.
 */
export function filtrerOptions<T extends OptionListEntry>(
  options: readonly T[],
  terme: string,
): T[] {
  const mots = searchField(terme.trim()).split(/\s+/).filter((mot) => mot !== '');
  if (mots.length === 0) return [...options];
  return options.filter((option) => {
    const textes = [option.label, option.detail ?? '', option.prefixe ?? '', option.motsCles ?? ''].map(
      searchField,
    );
    return mots.every((mot) => textes.some((texte) => texte.includes(mot)));
  });
}

/**
 * LE RANG OU LE CURSEUR SE POSE, ET IL NE SE POSE JAMAIS SUR « AUCUN ».
 *
 * LE DEFAUT QU'IL FERME. L'entree qui DETACHE est en TETE (rang 0) : au montage
 * comme a chaque frappe, le curseur y retombait, et `Entree` DETACHAIT la fiche
 * liee au lieu de choisir la premiere reponse de la recherche. C'est le geste le
 * plus destructeur de la liste, servi par la touche la plus naturelle.
 *
 * « Aucun » RESTE ATTEIGNABLE, a la fleche du haut : c'est un choix, ce n'est
 * simplement pas un DEFAUT.
 *
 * @param rangDeLaValeur Le rang de la valeur courante dans `options`, ou -1.
 * @param aucun La liste propose-t-elle l'entree qui detache ?
 *
 * FONCTION PURE ET EXPORTEE EXPRES : un curseur mal pose ne casse rien a
 * l'ecran, il se voit au moment ou l'on appuie sur Entree, c'est-a-dire trop
 * tard.
 */
export function curseurInitial(rangDeLaValeur: number, aucun: boolean): number {
  return aucun ? Math.max(1, rangDeLaValeur + 1) : Math.max(0, rangDeLaValeur);
}

/**
 * LE RANG SUIVANT, borne aux entrees rendues.
 *
 * Le curseur reste DANS la liste : sans borne, il designerait une entree qui
 * n'est plus rendue (un mot vient de la raccourcir) et `Entree` ne choisirait
 * rien. Zero entree rendue rend 0, ce que la borne d'affichage neutralise.
 *
 * FONCTION PURE ET EXPORTEE EXPRES, pour la meme raison que `curseurInitial`.
 */
export function prochainCurseur(rang: number, delta: number, entrees: number): number {
  return Math.min(Math.max(entrees - 1, 0), Math.max(0, rang + delta));
}

/**
 * LA TRANCHE : le nombre d'entrees montees d'un coup.
 *
 * LA LISTE EST RENDUE PAR TRANCHES, et rien n'est coupe. L'annuaire d'un lien
 * peut porter des milliers de fiches (`PLAFOND_ANNUAIRE`) : les monter toutes a
 * l'ouverture coutait 2,4 s entre le clic et la liste, mesure faite a 1 500
 * fiches. On monte donc la premiere tranche, puis une de plus quand le
 * defilement approche du bas, ou quand le curseur clavier la depasse. Le JEU
 * complet (`entrees`) reste celui de la navigation et du choix : seul le rendu
 * est tranche, et toute entree s'atteint en defilant ou en cherchant.
 *
 * DEUX CENTS : bien plus que ce que le panneau montre (une dizaine de lignes),
 * donc personne n'atteint le bord d'une tranche avant que la suivante soit la.
 */
export const TRANCHE_OPTIONS = 200;

/**
 * LE NOMBRE D'ENTREES A MONTER POUR QUE LE RANG `rang` EXISTE : la tranche
 * entiere qui le contient. Jamais moins d'une tranche, meme sur une liste vide
 * ou un rang negatif.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : une entree active absente du DOM ne leve
 * rien, le surlignage disparait simplement, et `Entree` choisit une fiche que
 * personne ne voit.
 */
export function trancheDuRang(rang: number): number {
  return (Math.floor(Math.max(0, rang) / TRANCHE_OPTIONS) + 1) * TRANCHE_OPTIONS;
}

/**
 * LES BORNES MONTEES `[debut, fin)` APRES QU'UN RANG DOIT EXISTER : la plage
 * courante, etendue jusqu'a la tranche qui contient ce rang, vers le haut ou
 * vers le bas. Elle ne retrecit jamais (rien ne se demonte sous les yeux).
 */
export function plageAvecLeRang(plage: { debut: number; fin: number }, rang: number): { debut: number; fin: number } {
  const r = Math.max(0, rang);
  const debut = Math.min(plage.debut, Math.floor(r / TRANCHE_OPTIONS) * TRANCHE_OPTIONS);
  const fin = Math.max(plage.fin, trancheDuRang(r));
  return debut === plage.debut && fin === plage.fin ? plage : { debut, fin };
}

/**
 * LES BORNES DE HAUTEUR DU PANNEAU, en pixels.
 *
 * LE PLAFOND est celui d'avant (14 rem, l'ancien `max-h-56`) : quand la place
 * ne manque pas, la liste se comporte exactement comme avant. Le PLANCHER evite
 * qu'un espace minuscule ne reduise le panneau a un trait : mieux vaut deborder
 * un peu que rendre la liste inutilisable.
 */
const LISTE_HAUTEUR_MAX = 224;
const LISTE_HAUTEUR_MIN = 80;
/** L'air qu'on laisse entre le panneau et le bord de ce qui le borne. */
const MARGE_PANNEAU = 12;

/**
 * LE BAS ET LE HAUT DISPONIBLES autour de la liste : la fenetre, ou le
 * conteneur qui defile quand il y en a un.
 *
 * Une fiche vit dans la zone 5, qui defile : mesurer contre la seule fenetre
 * ferait ouvrir vers le bas une liste que le conteneur rogne, et la moitie des
 * entrees serait inatteignable.
 */
function bornesAutour(depart: HTMLElement | null): { haut: number; bas: number } {
  let noeud = depart?.parentElement ?? null;
  while (noeud != null) {
    const style = window.getComputedStyle(noeud);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      const boite = noeud.getBoundingClientRect();
      return { haut: Math.max(0, boite.top), bas: Math.min(window.innerHeight, boite.bottom) };
    }
    noeud = noeud.parentElement;
  }
  return { haut: 0, bas: window.innerHeight };
}

/**
 * LE RETOURNEMENT HORIZONTAL : le panneau doit-il s'aligner sur le bord DROIT
 * de sa boite d'ancrage plutot que sur le gauche ?
 *
 * POURQUOI IL EXISTE. Le panneau s'ouvre vers la DROITE (`left-0`), ce qui va
 * de soi pour une case de fiche : elle a toute la largeur de sa colonne devant
 * elle. Mais la meme liste sert un bouton colle au bord DROIT d'une carte
 * (« Lier une fiche existante ») : mesure a 1440 px, elle allait de x=1350 a
 * x=1607, sans barre de defilement, donc les deux tiers de chaque libelle
 * etaient simplement coupes. C'est le pendant de `dessus` sur l'autre axe (il
 * se mesure en revanche plus tard, voir l'effet qui l'appelle).
 *
 * DEUX CONDITIONS, COMME `dessus` : il faut que ca DEBORDE (sinon on ne touche
 * a rien, et le selecteur d'un champ de lien ne bouge pas d'un pixel), et il
 * faut que l'autre bord soit MIEUX. Un panneau plus large que la place a gauche
 * de la boite passerait de coupe a droite a coupe a gauche, ce qui n'est pas un
 * progres : on le laisse alors ou il est, deborder du cote ou l'on regarde.
 *
 * L'ANCRE, ICI, EST LA BOITE POSITIONNEE contre laquelle `left-0` et `right-0`
 * se resolvent (le parent `relative` fourni par l'appelant), pas la case : c'est
 * elle qui fixe les deux places possibles.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : une liste posee hors de la fenetre ne leve
 * rien, ne se journalise nulle part et ne se voit que si l'on ouvre l'ecran au
 * bon endroit, a la bonne largeur.
 */
export function retournementHorizontal(mesures: {
  gaucheDeLAncre: number;
  droiteDeLAncre: number;
  largeurDuPanneau: number;
  largeurDeLaFenetre: number;
}): boolean {
  const { gaucheDeLAncre, droiteDeLAncre, largeurDuPanneau, largeurDeLaFenetre } = mesures;
  const debordeADroite = gaucheDeLAncre + largeurDuPanneau > largeurDeLaFenetre;
  const deborderaitAGauche = droiteDeLAncre - largeurDuPanneau < 0;
  return debordeADroite && !deborderaitAGauche;
}

export interface OptionListProps {
  /** Les entrees proposees, deja dans l'ordre voulu par l'appelant. */
  options: readonly OptionListEntry[];
  /** La valeur COURANTE, celle que la coche marque. `null` = aucune. */
  value: string | null;
  /**
   * Une entree choisie. `null` vient de l'entree qui DETACHE (`aucun`).
   * `parEntree` : le choix est venu de la touche `Entree`, pas d'un clic.
   */
  onPick(value: string | null, parEntree?: boolean): void;
  /** Fermer sans choisir : Echap, Tab, clic dehors. */
  onClose(): void;
  /**
   * Le texte du champ, redessine par-dessus la case pour qu'elle garde sa place
   * pendant que la liste est ouverte. Absent quand l'appelant montre DEJA son
   * propre element (la pastille de statut de l'en-tete) : on n'en dessine pas
   * un second par-dessus.
   */
  anchorLabel?: ReactNode;
  /** Un champ de recherche en tete, qui filtre `label` et `detail`. */
  recherche?: boolean;
  /**
   * LE LIBELLE DE L'ENTREE QUI DETACHE (« Aucun »), en tete de liste. Absent :
   * la liste ne propose pas de detacher, et c'est le cas d'un catalogue, ou la
   * valeur vide est deja une option comme une autre.
   */
  aucun?: string;
  /** Les options ne sont pas encore la : un squelette, pas une liste vide. */
  chargement?: boolean;
  /** Le mot quand rien ne correspond. Defaut : « Aucun resultat ». */
  vide?: string;
  /**
   * LA RECHERCHE EST PARTIELLE : la portee de ce qui a ete lu, sous le champ de
   * recherche (un annuaire au plafond). Absente : la liste porte tout le jeu,
   * et rien ne s'affiche.
   */
  partiel?: string;
  /**
   * LA RAISON d'une lecture impossible. Presente, elle GAGNE sur tout le
   * reste : la liste rend un libelle grise qui la porte en `title` (G16),
   * plutot qu'un panneau vide qui ferait croire a un jeu sans fiches.
   */
  erreur?: string;
  /**
   * LE LIBELLE DE CET ECHEC, quand ce n'est pas une LECTURE qui a echoue.
   * Defaut : « Lecture impossible ». Un libelle, pas une phrase (G26) : la
   * raison complete reste dans le `title`.
   */
  libelleErreur?: string;
  /**
   * LE PIED DE LA LISTE : des gestes poses sous les entrees, qui ne defilent
   * pas avec elles. Recoit le terme tape, pour le transmettre (la fenetre de
   * choix d'un champ de lien s'ouvre sur la recherche en cours). C'est la que
   * le champ de lien pose ses deux gestes, Lier et Ajouter (`GestesDeLien`).
   *
   * LE CLAVIER Y ENTRE PAR `Tab` depuis la liste ou la recherche, en ressort
   * par `Tab` sur le dernier bouton (la liste se ferme) ou `Maj+Tab` sur le
   * premier (retour a la recherche) ; `Echap` ferme partout.
   */
  pied?: (terme: string) => ReactNode;
  /** Presentation resserree pour les recherches de fiches de reference. */
  compact?: boolean;
  /**
   * LES VALEURS COCHEES D'UN CHOIX MULTIPLE. Presentes, la liste devient
   * MULTIPLE : la coche marque chacune d'elles (et plus `value`), la liste
   * s'annonce `aria-multiselectable`, et choisir une entree ne veut plus dire
   * « c'est celle-la » mais « bascule celle-la ». La liste ne se referme donc
   * pas au choix : c'est l'appelant qui tient les coches, et qui ecrit a la
   * fermeture (`onClose`).
   *
   * UNE PROP DE PLUS, PAS UNE SECONDE LISTE : le panneau, le clavier, le clic
   * dehors et le retournement sont exactement ceux du catalogue. Deux listes
   * deroulantes maison auraient diverge au premier ajustement.
   */
  coches?: readonly string[];
}

export function OptionList({
  options,
  value,
  onPick,
  onClose,
  anchorLabel,
  recherche = false,
  aucun,
  chargement = false,
  vide = 'Aucun résultat',
  partiel,
  erreur,
  libelleErreur = LECTURE_IMPOSSIBLE,
  pied,
  compact = false,
  coches,
}: OptionListProps): JSX.Element {
  const racineRef = useRef<HTMLSpanElement>(null);
  const panneauRef = useRef<HTMLDivElement>(null);
  const listeRef = useRef<HTMLDivElement>(null);
  const champRef = useRef<HTMLInputElement>(null);
  const piedRef = useRef<HTMLDivElement>(null);
  /* L'ENTREE ACTIVE, pour la ramener sous les yeux au clavier (voir plus bas). */
  const actifRef = useRef<HTMLButtonElement | null>(null);
  /* LA CASE D'OU L'ON VIENT : Echap doit lui rendre le focus. Sans ca, le focus
     retombe sur le `body`, et le geste suivant (Echap pour fermer la fiche,
     Alt+Fleche pour changer d'element) ne trouve plus personne. */
  const ancreRef = useRef<Element | null>(null);
  const identifiant = useId();
  const [terme, setTerme] = useState('');
  const [dessus, setDessus] = useState(false);
  /* LE RETOURNEMENT DE L'AXE HORIZONTAL (voir `retournementHorizontal` et
     l'effet qui le mesure). Faux tant qu'on n'a pas mesure : le panneau s'ouvre
     vers la droite, comme avant. */
  const [droite, setDroite] = useState(false);
  /* LA HAUTEUR MESUREE de la zone qui defile, en pixels. `null` tant qu'on n'a
     pas mesure : le plafond de repli s'applique alors, comme avant. */
  const [hauteurListe, setHauteurListe] = useState<number | null>(null);

  const courante = value == null ? '' : String(value);
  const multiple = coches !== undefined;
  /* LA COCHE D'UNE ENTREE : sa valeur est-elle LA valeur (un catalogue), ou
     l'une des valeurs cochees (un choix multiple) ? */
  const estCochee = (valeur: string): boolean =>
    multiple ? coches.includes(valeur) : valeur === courante;
  const gestesDuPied = pied?.(terme) ?? null;
  /* LES BOUTONS DU PIED, dans l'ordre du DOM : c'est entre eux que `Tab` circule. */
  const boutonsDuPied = (): HTMLButtonElement[] =>
    [...(piedRef.current?.querySelectorAll('button') ?? [])];
  const visibles = recherche ? filtrerOptions(options, terme) : [...options];
  /* L'ENTREE QUI DETACHE VIENT EN TETE, et elle ne se filtre pas : elle n'est
     pas une fiche, c'est le geste « plus de fiche ». La chercher en tapant
     n'aurait aucun sens, la faire disparaitre sous un mot en aurait encore
     moins. */
  const entrees: OptionListEntry[] =
    aucun === undefined ? visibles : [{ value: '', label: aucun }, ...visibles];
  const [curseur, setCurseur] = useState(() =>
    curseurInitial(
      options.findIndex((option) => estCochee(option.value)),
      aucun !== undefined,
    ),
  );
  /* Le curseur reste DANS la liste quand un mot la raccourcit : sans ca, il
     designerait une entree qui n'est plus rendue, et `Entree` ne choisirait
     rien. */
  const rang = Math.min(curseur, Math.max(0, entrees.length - 1));
  /* LES ENTREES MONTEES, une plage `[debut, fin)` (voir `TRANCHE_OPTIONS`).
     Elle ne fait que GRANDIR tant que le terme ne change pas : rien ne se
     demonte sous les yeux. Au depart, la SEULE tranche qui contient la valeur
     cochee, pour qu'elle soit la, surlignee, des l'ouverture, sans monter tout
     ce qui la precede (une fiche choisie au rang 1 300 montait 1 400 entrees).
     Celles d'avant se montent quand on remonte la liste. */
  const [plage, setPlage] = useState(() => plageAvecLeRang({ debut: Number.MAX_SAFE_INTEGER, fin: 0 }, curseur));
  /* ET LE RANG ACTIF EST TOUJOURS MONTE, quoi que dise l'etat : c'est lui que
     `actifRef` ramene sous les yeux, et une entree absente du DOM ne se ramene
     pas. */
  const montee = plageAvecLeRang(plage, rang);
  const premiereRendue = Math.min(montee.debut, entrees.length);
  const rendues = Math.min(entrees.length, montee.fin);
  /* MONTER LES ENTREES DU DESSUS NE DOIT PAS FAIRE SAUTER LA LISTE : la hauteur
     ajoutee au-dessus est rendue au defilement, dans la meme image. */
  const hauteurAvantAjoutRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const liste = listeRef.current;
    const avant = hauteurAvantAjoutRef.current;
    hauteurAvantAjoutRef.current = null;
    if (liste == null || avant == null) return;
    liste.scrollTop += liste.scrollHeight - avant;
  }, [premiereRendue]);

  // Le clavier va au CHAMP quand il y en a un, sinon a la liste elle-meme.
  useEffect(() => {
    ancreRef.current = document.activeElement;
    if (champRef.current != null) champRef.current.focus();
    else listeRef.current?.focus();
  }, []);

  /*
   * LE RETOURNEMENT VERTICAL ET LA HAUTEUR, MESURES UNE FOIS AU MONTAGE. Une
   * fiche peut descendre bas dans une zone 5 courte : ouverte vers le bas, la
   * liste sortirait du conteneur qui defile et la moitie de ses entrees serait
   * inatteignable.
   *
   * ET LA HAUTEUR SUIT LA MESURE, pas un `max-h` fixe. Le plafond ecrit en dur
   * (14 rem) ne connait pas la place disponible : dans un `Modal` court, le
   * panneau depassait la fenetre et ses dernieres entrees etaient rognees, sans
   * barre de defilement a l'endroit ou l'on regarde. On BORNE donc la zone qui
   * defile a ce qui reste, en gardant l'ancien plafond quand la place ne manque
   * pas (le rendu ne bouge alors pas d'un pixel).
   *
   * LE PANNEAU, PAS LA LISTE : c'est lui qui porte le champ de recherche, et
   * c'est sa hauteur totale qui deborde. On retire donc l'en-tete de la place
   * mesuree avant de la donner a la liste.
   *
   * EN EFFET DE MISE EN PAGE, pas un effet ordinaire : un effet ordinaire
   * tourne apres la peinture, donc la liste s'afficherait un instant en bas
   * avant de sauter en haut, a l'endroit precis ou l'oeil se pose.
   */
  useLayoutEffect(() => {
    const panneau = panneauRef.current;
    const liste = listeRef.current;
    const racine = racineRef.current;
    if (panneau == null || liste == null || racine == null) return;
    const bornes = bornesAutour(racine);
    const ancre = racine.getBoundingClientRect();
    const placeDessous = bornes.bas - ancre.bottom - MARGE_PANNEAU;
    const placeDessus = ancre.top - bornes.haut - MARGE_PANNEAU;
    const versLeHaut =
      panneau.getBoundingClientRect().bottom > bornes.bas && placeDessus > placeDessous;
    if (versLeHaut) setDessus(true);
    /* L'EN-TETE : tout ce que le panneau porte AUTOUR de la zone qui defile
       (le champ de recherche et ses filets). Mesure, jamais devine : il vaut
       zero sur une liste nue et une quarantaine de pixels avec la recherche. */
    const enTete = Math.max(0, panneau.offsetHeight - liste.offsetHeight);
    const place = (versLeHaut ? placeDessus : placeDessous) - enTete;
    setHauteurListe(
      Math.max(
        LISTE_HAUTEUR_MIN,
        Math.min(compact ? 192 : LISTE_HAUTEUR_MAX, Math.round(place)),
      ),
    );
  }, [compact, gestesDuPied != null]);

  /*
   * LE RETOURNEMENT HORIZONTAL, MESURE A CHAQUE RENDU ET NON AU SEUL MONTAGE.
   * C'est la seule difference avec l'axe vertical, et elle vient de ce qui fixe
   * chaque dimension : la HAUTEUR disponible est connue des le montage (elle ne
   * depend que de l'ancre et de ce qui l'entoure), la LARGEUR du panneau, elle,
   * depend de son CONTENU. Au montage il ne porte qu'un squelette et il prend
   * la largeur de l'ancre (32 px sous un bouton carre) : mesure la, il ne
   * deborde jamais, et la liste venait ensuite se poser hors de l'ecran.
   *
   * IL NE SE DEFAIT PAS, comme `dessus`, et la mesure s'arrete avec lui : un
   * mot tape dans la recherche raccourcit les libelles donc le panneau, et le
   * rendre alors a son bord d'origine le ferait sauter sous le curseur entre
   * deux frappes.
   *
   * CONTRE LA FENETRE, pas contre `bornesAutour` : ce qui rogne la hauteur est
   * le conteneur qui DEFILE, et il ne defile que verticalement ; a
   * l'horizontale, ce qui coupe est le bord de l'ecran.
   *
   * `offsetWidth` ET NON `getBoundingClientRect()` : le panneau s'ouvre sur une
   * animation qui le met a l'echelle (`taki-anim-pop`, scale 0.98), et la boite
   * client porte cette transformation. On mesure la place qu'il PREND dans la
   * mise en page, pas celle qu'il occupe a la frame ou on regarde.
   */
  useLayoutEffect(() => {
    if (droite) return;
    const panneau = panneauRef.current;
    if (panneau == null) return;
    /* LA BOITE D'ANCRAGE est le parent positionne (`offsetParent`), celui-la
       meme contre lequel `left-0` et `right-0` se resolvent : le mesurer, c'est
       connaitre les deux places possibles sans rien deviner de l'appelant. */
    const ancrage = (panneau.offsetParent ?? document.documentElement).getBoundingClientRect();
    const versLaDroite = retournementHorizontal({
      gaucheDeLAncre: ancrage.left,
      droiteDeLAncre: ancrage.right,
      largeurDuPanneau: panneau.offsetWidth,
      largeurDeLaFenetre: window.innerWidth,
    });
    if (versLaDroite) setDroite(true);
  });

  /*
   * L'ENTREE ACTIVE RESTE SOUS LES YEUX. Le panneau defile ; sans ca, les
   * fleches deplacaient un surlignage invisible des la sixieme entree, et
   * `Entree` choisissait une fiche que personne n'avait vue. `block: 'nearest'`
   * ne bouge la vue que si l'entree est hors champ : le panneau ne sursaute pas
   * a chaque pas.
   */
  useEffect(() => {
    actifRef.current?.scrollIntoView({ block: 'nearest' });
  }, [rang]);

  /* Fermeture au clic dehors, en CAPTURE : cliquer un autre champ doit fermer
     cette liste puis ouvrir l'autre, dans le meme geste. L'ancre fait partie de
     `racineRef`, donc cliquer dessus ne referme pas par ce chemin : c'est le
     `onClick` de l'ancre qui s'en charge, sinon la liste se rouvrirait aussitot. */
  useEffect(() => {
    const dehors = (event: PointerEvent): void => {
      if (racineRef.current != null && !racineRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('pointerdown', dehors, true);
    return () => document.removeEventListener('pointerdown', dehors, true);
  }, [onClose]);

  const bouger = (delta: number): void => {
    const suivant = prochainCurseur(rang, delta, entrees.length);
    setCurseur(suivant);
    // Le curseur qui sort de la plage montee l'agrandit, et ca reste acquis.
    setPlage((avant) => plageAvecLeRang(avant, suivant));
  };
  /* LE DEFILEMENT QUI APPROCHE DU BAS monte la tranche suivante : a moins d'une
     hauteur de liste du bas, donc avant que l'oeil n'y arrive. */
  const auDefilement = (): void => {
    const liste = listeRef.current;
    if (liste == null) return;
    const resteEnBas = liste.scrollHeight - liste.scrollTop - liste.clientHeight;
    /* UNE TRANCHE DE PLUS QUE CE RENDU, PAS UNE PAR EVENEMENT : plusieurs
       defilements arrivent avant le rendu suivant, et chacun en aurait ajoute
       une. */
    if (rendues < entrees.length && resteEnBas < liste.clientHeight) {
      const cible = rendues;
      setPlage((avant) => plageAvecLeRang(avant, cible));
    }
    /* ET VERS LE HAUT, quand la liste s'ouvre sur une valeur loin du debut. */
    if (premiereRendue > 0 && liste.scrollTop < liste.clientHeight && hauteurAvantAjoutRef.current == null) {
      hauteurAvantAjoutRef.current = liste.scrollHeight;
      const cible = premiereRendue - 1;
      setPlage((avant) => plageAvecLeRang(avant, cible));
    }
  };
  const choisir = (option: OptionListEntry | undefined, parEntree = false): void => {
    if (option == null || option.inactif !== undefined) return;
    const valeur = option.value === '' ? null : option.value;
    if (parEntree) onPick(valeur, true);
    else onPick(valeur);
  };
  /* ECHAP FERME LA LISTE, ET RIEN D'AUTRE. `stopPropagation` : hors editeur, la
     meme touche ferme la FICHE (`useScreen`), et les deux gestes doivent rester
     distincts. Le focus revient a la case, pour que le geste d'apres parte du
     bon endroit. */
  const fermerAuClavier = (): void => {
    onClose();
    if (ancreRef.current instanceof HTMLElement) ancreRef.current.focus();
  };

  const auClavier = (event: {
    key: string;
    shiftKey: boolean;
    preventDefault(): void;
    stopPropagation(): void;
  }): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      fermerAuClavier();
      return;
    }
    if (event.key === 'Tab') {
      const premier = boutonsDuPied()[0];
      if (premier !== undefined && !event.shiftKey) {
        event.preventDefault();
        premier.focus();
        return;
      }
      onClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      bouger(1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      bouger(-1);
      return;
    }
    if (event.key === 'Enter' || (event.key === ' ' && !recherche)) {
      // L'espace choisit sur une liste NUE ; dans un champ de recherche, il
      // separe deux mots.
      event.preventDefault();
      /* UNE RECHERCHE QUI NE TROUVE RIEN NE CHOISIT RIEN. Sans ce garde, la
         seule entree rendue serait « Aucun », le curseur y retomberait par la
         borne d'affichage, et taper un mot inconnu puis `Entree` DETACHERAIT la
         fiche liee. Le geste le plus destructeur, sur la frappe la plus banale. */
      if (recherche && terme.trim() !== '' && visibles.length === 0) return;
      choisir(entrees[rang], event.key === 'Enter');
    }
  };

  const idOption = (index: number): string => `${identifiant}-o${index}`;
  /* L'ENTREE ACTIVE N'EXISTE QUE QUAND LA LISTE EST RENDUE : un squelette, une
     raison d'echec ou zero resultat ne portent aucune option, et pointer un id
     qui n'existe pas laisse le lecteur d'ecran annoncer du vide. */
  const optionActive =
    erreur === undefined && !chargement && entrees.length > 0 ? idOption(rang) : undefined;

  const corps = ((): ReactNode => {
    if (erreur !== undefined) {
      /* UN LIBELLE GRISE QUI PORTE SA RAISON (G16). Ce n'est pas un controle
         desactive : il recoit donc bien le survol, et son `title` s'affiche. */
      return (
        <p title={erreur} className="px-2.5 py-2 text-sm text-ink-300">
          {libelleErreur}
        </p>
      );
    }
    if (chargement) {
      /* TROIS LIGNES, decoratives : `aria-busy` porte l'etat. Une liste vide le
         temps du chargement ferait lire « aucune fiche » sur une lecture qui
         n'a pas encore repondu. */
      return (
        <div aria-busy className="px-2.5 py-1.5">
          <span aria-hidden className="flex flex-col gap-2">
            <span className="taki-skeleton block h-3 w-3/4 rounded-xs" />
            <span className="taki-skeleton block h-3 w-1/2 rounded-xs" />
            <span className="taki-skeleton block h-3 w-2/3 rounded-xs" />
          </span>
        </div>
      );
    }
    if (entrees.length === 0) {
      return <p className="px-2.5 py-2 text-sm text-ink-300">{vide}</p>;
    }
    return (
      <>
        {entrees.slice(premiereRendue, rendues).map((option, place) => {
          const index = premiereRendue + place;
          return (
          <Fragment key={`${index}-${option.value}`}>
          {/* LE TITRE DU GROUPE, au changement de groupe seulement. Hors de la
              navigation : ni curseur, ni clic, ni annonce comme une option. */}
          {option.groupe !== undefined && option.groupe !== entrees[index - 1]?.groupe && (
            <p
              role="presentation"
              className={cn(
                'px-2.5 pb-1 pt-2 text-[0.6875rem] font-semibold text-ink-400',
                compact && 'px-3',
              )}
            >
              {option.groupe}
            </p>
          )}
          <button
            id={idOption(index)}
            /* Seule l'entree ACTIVE porte le ref : c'est elle qu'on ramene sous les
               yeux quand le clavier deplace le curseur. */
            ref={index === rang ? actifRef : null}
            type="button"
            role="option"
            /* LA PLACE DANS LA LISTE ENTIERE : seule une tranche est montee, et
               le DOM ne dit plus a lui seul ou l'on est (relecture Astra). */
            aria-setsize={entrees.length}
            aria-posinset={index + 1}
            aria-selected={estCochee(option.value)}
            aria-disabled={option.inactif !== undefined || undefined}
            title={option.inactif}
            tabIndex={compact ? -1 : undefined}
            onMouseEnter={() => setCurseur(index)}
            onClick={() => choisir(option)}
            className={cn(
              'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm transition-colors',
              compact && 'mx-1 w-[calc(100%-0.5rem)] rounded-sm px-2 py-1 text-[0.8125rem]',
              index === rang ? 'bg-surface-3 text-ink-900' : 'text-ink-700',
              option.inactif !== undefined && 'cursor-not-allowed text-ink-300',
            )}
          >
            {/* Gouttiere de largeur FIXE : la coche ne decale pas les libelles. */}
            <span
              className={cn(
                'flex w-3.5 shrink-0 items-center text-accent-dot',
                compact && 'h-4 w-4 justify-center text-ink-900',
              )}
            >
              {estCochee(option.value) && <IconCheck size={compact ? 10 : 13} />}
            </span>
            <span className="flex min-w-0 flex-col">
              {/* LE CHEMIN SUR SA LIGNE, au-dessus du mot : en ligne, il
                  rognait le nom de la tache (« Projet X › Tâche Y » tronque
                  avant « Tâche »), et toutes les taches d'un projet se
                  ressemblaient (recette 2). Chaque ligne tronque seule. */}
              {option.prefixe !== undefined && option.prefixe !== '' && (
                <span className={cn('min-w-0 truncate text-xs text-ink-400', compact && 'text-[0.6875rem]')}>
                  {`${option.prefixe} ›`}
                </span>
              )}
              <span className={cn('min-w-0 truncate', option.attenue === true && 'text-ink-500')}>{option.label}</span>
              {option.detail !== undefined && option.detail !== '' && (
                <span className={cn('min-w-0 truncate text-xs text-ink-500', compact && 'text-[0.6875rem]')}>
                  {option.detail}
                </span>
              )}
            </span>
          </button>
          </Fragment>
          );
        })}
        {compact && recherche && terme.trim() !== '' && visibles.length === 0 && (
          <p className="px-3 py-2 text-[0.8125rem] text-ink-300">{vide}</p>
        )}
      </>
    );
  })();

  return (
    <span ref={racineRef}>
      {anchorLabel !== undefined && (
        <span
          role="button"
          tabIndex={-1}
          onClick={onClose}
          /* `min-h-7`, la boite d'un champ de fiche : l'ancre RECOUVRE la case,
             donc elle doit en avoir la hauteur, sinon ouvrir la liste ferait
             sauter la ligne sous les yeux. */
          className={cn(
            '-mx-1.5 absolute inset-x-0 top-1/2 z-20 flex min-h-7 -translate-y-1/2 items-center rounded-sm px-1.5',
            'bg-surface ring-1 ring-inset ring-accent-border',
          )}
        >
          <span className="min-w-0 truncate">{anchorLabel}</span>
        </span>
      )}
      <div
        ref={panneauRef}
        /* LA LISTE GARDE SON ECHAP : il la ferme, elle seule, et pas la fenetre
           qui la porte (`useDialogFocus` le lui laisse). */
        data-garde-echap=""
        className={cn(
          /* JAMAIS PLUS LARGE QUE L'ECRAN : sur un telephone, un panneau de
             22rem calé a droite d'une case etroite debordait a gauche et
             coupait le debut de ses lignes (recette 2). */
          'taki-anim-pop absolute z-30 w-max min-w-full max-w-[min(18rem,calc(100vw_-_1.5rem))]',
          /* AVEC RECHERCHE, UN PLANCHER : un panneau a la largeur d'une case
             etroite ne laisse pas la place a un champ de saisie et a un
             sous-titre. `max(100%, ...)` garde AUSSI le plancher de la case,
             que `min-w-full` posait seul (tailwind-merge ne garde qu'un
             `min-w-` : deux classes se seraient annulees). */
          recherche && 'min-w-[min(max(100%,15rem),calc(100vw_-_1.5rem))]',
          compact && 'min-w-[min(max(100%,18rem),calc(100vw_-_1.5rem))] max-w-[min(22rem,calc(100vw_-_1.5rem))]',
          /* SUR UN TELEPHONE, LA LARGEUR DE SA CASE, ni plus ni moins : plus
             large, il debordait de la fenetre qui le porte et la faisait
             defiler d'un cran (« ojet / Tâche », recette 2). Les lignes
             tronquent dedans. */
          'max-sm:w-full max-sm:min-w-full max-sm:max-w-full',
          // RETOURNEMENT : au-dessus quand le bas manque, sinon sous le champ.
          dessus ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          /* ET SUR L'AUTRE AXE : cale a droite quand la droite manque, sinon
             ouvert vers la droite depuis le bord gauche, comme toujours. Les
             deux classes s'EXCLUENT : posees ensemble, elles etireraient le
             panneau a la largeur de son ancre, et `tailwind-merge` ne les
             departagerait pas (ce sont deux proprietes differentes). */
          droite ? 'right-0' : 'left-0',
          'overflow-hidden rounded-md border border-border bg-surface shadow-pop',
        )}
      >
        {recherche && (
          <div className="border-b border-border-soft p-1.5">
            <Input
              ref={champRef}
              className={cn(
                'h-7 rounded-sm',
                compact &&
                  'focus-within:border-accent-border focus-within:shadow-none focus-within:ring-1 focus-within:ring-inset focus-within:ring-accent-border',
              )}
              leftIcon={<IconSearch size={13} />}
              placeholder="Rechercher"
              {...(compact ? { 'aria-label': 'Rechercher une fiche' } : {})}
              role="combobox"
              aria-expanded
              aria-controls={`${identifiant}-liste`}
              aria-activedescendant={optionActive}
              value={terme}
              onChange={(event) => {
                setTerme(event.target.value);
                /* LE CURSEUR REPART SUR LA PREMIERE OPTION, jamais sur
                   « Aucun » : `Entree` choisit ce qu'on vient de chercher, il
                   ne detache pas (voir `curseurInitial`). */
                setCurseur(curseurInitial(-1, aucun !== undefined));
                // Et le rendu repart a la premiere tranche.
                setPlage({ debut: 0, fin: TRANCHE_OPTIONS });
              }}
              onKeyDown={auClavier}
            />
            {partiel !== undefined && (
              <p role="note" className="px-1.5 pt-1 text-[0.6875rem] leading-4 text-ink-400">
                {partiel}
              </p>
            )}
          </div>
        )}
        <div
          ref={listeRef}
          id={`${identifiant}-liste`}
          role="listbox"
          tabIndex={-1}
          {...(multiple ? { 'aria-multiselectable': true } : {})}
          /* SANS CHAMP DE RECHERCHE, C'EST CETTE BOITE QUI A LE FOCUS : c'est
             donc elle qui doit annoncer l'entree active, sinon un lecteur
             d'ecran ne suit pas les fleches. Avec la recherche, l'attribut vit
             sur le champ, et le poser aux deux endroits annoncerait deux fois. */
          {...(recherche ? {} : { 'aria-activedescendant': optionActive })}
          onKeyDown={auClavier}
          onScroll={auDefilement}
          /* LA HAUTEUR EST MESUREE (voir l'effet de mise en page) ; le plafond
             en classe reste le repli du premier rendu, avant toute mesure. */
          style={hauteurListe == null ? undefined : { maxHeight: `${hauteurListe}px` }}
          className={cn(
            'max-h-56 overflow-y-auto py-1 focus-visible:outline-none',
            compact && 'max-h-48',
          )}
        >
          {corps}
        </div>
        {gestesDuPied != null && (
          <div
            ref={piedRef}
            /* Les gestes du champ commencent au bord gauche du pied. */
            className="flex shrink-0 justify-start border-t border-border-soft bg-surface-1 p-2.5"
            onKeyDown={(event) => {
              const boutons = boutonsDuPied();
              const rangDuFocus = boutons.findIndex((bouton) => bouton === event.target);
              if (event.key === 'Escape') {
                event.stopPropagation();
                event.preventDefault();
                fermerAuClavier();
              } else if (event.key === 'Tab' && event.shiftKey && rangDuFocus <= 0) {
                event.preventDefault();
                if (champRef.current != null) champRef.current.focus();
                else listeRef.current?.focus();
              } else if (event.key === 'Tab' && !event.shiftKey && rangDuFocus === boutons.length - 1) {
                onClose();
              }
            }}
          >
            {gestesDuPied}
          </div>
        )}
      </div>
    </span>
  );
}
